import {clearCachedToken, getAuthToken, getCachedToken} from '../api/token'
import instanceConfig from '../instances.json'
import ArrowUp from '../assets/angle-up-solid.svg'
import ArrowDown from '../assets/angle-down-solid.svg'
import instance from '../instances.json'
import {storeSDKEvent} from '../util/database'
import {Settings} from '../util/settings'
import {hasFeature} from '../features'
import {getSDKVersion} from '../util/helpers'
import {parseFlushIntervalInput} from '../util/flush-interval'
import {publishSDKEvent} from '../util/sdk-event-bus'

// `setApiProtocol` must be called before the SDK is first initialised, so analytics settings are applied only once
// per page load. Changing them reloads the page.
let analyticsSettingsApplied = false

const applyAnalyticsSettingsBeforeInitialise = sdkFeatures => {
  if (
    analyticsSettingsApplied ||
    !hasFeature('analyticsEventFlush', getSDKVersion())
  ) {
    return
  }
  analyticsSettingsApplied = true

  // WebSockets is the SDK default, and setting it explicitly would override the "Disable WebSockets" feature setting.
  if (sdkFeatures.apiProtocol === 'http') {
    try {
      console.log("Calling AtomicSDK.setApiProtocol('http') before initialise.")
      window.AtomicSDK.setApiProtocol('http')
    } catch (error) {
      console.error('Failed to set the SDK API protocol.', error)
    }
  }

  if (sdkFeatures.eventFlushIntervalOnLoad) {
    const {value, description} = parseFlushIntervalInput(
      sdkFeatures.eventFlushIntervalOnLoad
    )
    console.log(
      `Calling AtomicSDK.setEventFlushInterval(${description}) before initialise.`
    )
    window.AtomicSDK.setEventFlushInterval(value)
  }
}

/**
 * Callbacks that log to the console with the name of the container that called them, so that callbacks from containers
 * sharing a page can be told apart.
 * @param {string} label The container name included in each log.
 * @param {Function} [onCardCountChanged] Also called with the card count.
 */
const loggedCallbacks = (label, onCardCountChanged) => ({
  onSizeChanged: (width, height) => {
    console.log(`[${label}] onSizeChanged: ${width} x ${height}`)
  },
  onCardEvent: event => {
    console.log(`[${label}] onCardEvent: ${event.type}`)
  },
  onCardCountChanged: count => {
    console.log(`[${label}] onCardCountChanged: ${count}`)
    if (onCardCountChanged) {
      onCardCountChanged(count)
    }
  },
  onLauncherToggled: isOpen => {
    if (instance.identifier !== 'beta') {
      console.log(`[${label}] onLauncherToggled: ${isOpen}`)
    }
  },
})

/**
 * Returns a copy of the config with the horizontal layout enabled or disabled, without changing the original.
 */
const withHorizontalLayout = (config, enabled) => ({
  ...config,
  features: {
    ...config.features,
    horizontalContainerConfig: {
      ...config.features.horizontalContainerConfig,
      enabled,
    },
  },
  ...(config.horizontalContainerConfig
    ? {
        horizontalContainerConfig: {
          ...config.horizontalContainerConfig,
          enabled,
        },
      }
    : {}),
})

/**
 * Mounts a single card, a standalone and a launcher container on the same stream container, so that containers sharing
 * a page can be tested together. Only the launcher's card count is shown on the launcher badge.
 * @returns An object that stops, or updates the runtime variables of, all three containers.
 */
const createMultipleContainers = (config, onCardCountChanged) => {
  const containers = [
    window.AtomicSDK.singleCard(document.getElementById('multi-single-card'), {
      ...config,
      ...loggedCallbacks('Single card'),
    }),
    window.AtomicSDK.embed(document.getElementById('multi-standalone'), {
      ...withHorizontalLayout(config, false),
      ...loggedCallbacks('Standalone'),
    }),
    window.AtomicSDK.launch({
      ...config,
      ...loggedCallbacks('Launcher', onCardCountChanged),
    }),
  ]
  const launcher = containers[2]

  return {
    setOpen: open => launcher.setOpen(open),
    stop: () => containers.forEach(container => container.stop()),
    updateVariables: () =>
      containers.forEach(container => container.updateVariables()),
  }
}

const EMBED_MODE_LABELS = {
  standalone: 'Standalone',
  'standalone-horizontal': 'Standalone (horizontal)',
  launcher: 'Launcher',
  'single-card': 'Single card',
  headless: 'Headless',
  modal: 'Modal',
}

// Copyright © 2019 Atomic.io Limited. All rights reserved.
const createStreamContainer = (
  apiHost,
  streamContainerId,
  onCardCountChanged,
  embedMode,
  apiKey,
  siteId,
  sdkFeatures,
  testHost,
  cardFeedHandler
) => {
  if (
    instance.identifier !== 'beta' &&
    hasFeature('customTokenUserId', getSDKVersion()) &&
    Settings.configuration.customTokenUserIdField
  ) {
    window.AtomicSDK.setTokenUserIdAttribute(
      Settings.configuration.customTokenUserIdField
    )
  }

  if (window.AtomicSDK.singleCard) {
    // The HTTP protocol also disables WebSockets, so it is kept when the container is recreated after initialising.
    const usesHttpProtocol =
      hasFeature('analyticsEventFlush', getSDKVersion()) &&
      sdkFeatures.apiProtocol === 'http'

    // Disable WebSockets if specified in config
    window.AtomicSDK.internalConfig = {
      ...window.AtomicSDK.internalConfig,
      webSocketDisabled: Boolean(
        sdkFeatures.webSocketDisabled || usesHttpProtocol
      ),
    }

    applyAnalyticsSettingsBeforeInitialise(sdkFeatures)

    // Newer SDK versions take the API host as part of initialise.
    window.AtomicSDK.initialise(apiHost, apiKey, siteId)
    // Set session delegate
    window.AtomicSDK.setSessionDelegate(async () => await getAuthToken())
  } else {
    // Older SDK versions have API host in the config object.
    window.AtomicSDK.initialise(apiKey, siteId)
  }

  if (window.AtomicSDK.setTestHostHeader) {
    // SDK version 0.19.0+ supports setting of the test host header for local development.
    window.AtomicSDK.setTestHostHeader(testHost)
  }

  if (hasFeature('sdkEventObserver', getSDKVersion())) {
    // The analytics events panel lists SDK events, so they are always observed when it is available.
    const showInAnalyticsPanel = hasFeature(
      'analyticsEventFlush',
      getSDKVersion()
    )

    if (showInAnalyticsPanel || sdkFeatures.observeSDKEvents) {
      window.AtomicSDK.observeSDKEvents(async event => {
        if (showInAnalyticsPanel) {
          publishSDKEvent(event)
        }
        if (!sdkFeatures.observeSDKEvents) {
          return
        }
        if (window.AtomicSDK.userMetricsTriggerEvents.has(event.eventName)) {
          window.AtomicSDK.requestUserMetrics().then(metrics => {
            const containerMetrics =
              metrics.metricsByContainer[streamContainerId]
            console.log('Container metrics triggered: ', containerMetrics)
          })
        }
        instanceConfig.retainSDKEvents &&
          (await storeSDKEvent(event.eventName, event))
      })
    }
  }

  if (sdkFeatures.simulateTokenError) {
    clearCachedToken()

    window.AtomicSDK.setSessionDelegate(async () => {
      if (!getCachedToken()) {
        // Cache auth token but return an error token this time.
        setTimeout(getAuthToken, 1000)
        return Promise.resolve('invalid.auth.token')
      }
      return await getAuthToken()
    })
  }
  const launcherButtonConfig = JSON.parse(
    sdkFeatures.customiseLauncherButton || '{}'
  )

  document.body.classList.toggle(
    'multi-container-mode',
    embedMode === 'multiple'
  )

  const config = {
    apiHost,
    streamContainerId,
    ...loggedCallbacks(
      EMBED_MODE_LABELS[embedMode] || embedMode,
      onCardCountChanged
    ),
    cardListRefreshInterval: 5000,
    enabledUiElements: {
      cardListToast: sdkFeatures.cardListToastEnabled,
      toastConfig: {
        ...(sdkFeatures.toastConfig
          ? {
              timeout: sdkFeatures.toastConfig.timeout,
              toastMessages: {
                submittedCard: sdkFeatures.toastConfig.submittedCard,
                dismissedCard: sdkFeatures.toastConfig.dismissedCard,
                snoozedCard: sdkFeatures.toastConfig.snoozedCard,
                feedbackReceived: sdkFeatures.toastConfig.feedbackReceived,
              },
            }
          : {}),
      },
      cardListHeader: sdkFeatures.cardListHeader,
      customContainerHeader: sdkFeatures.customContainerHeader && {
        scrollHeader: true,
        headerElement: `
        <div style="padding: 10px;background-color: dodgerBlue;border-radius: 5px;">
          <h1 style="color: white">Custom Header</h1>
        </div>
        `,
      },
      launcherButton: {
        ...launcherButtonConfig,
        openIconSrc:
          launcherButtonConfig.openIconSrc === 'custom' ? ArrowUp : '',
        closeIconSrc:
          launcherButtonConfig.closeIconSrc === 'custom' ? ArrowDown : '',
      },
    },
    customStrings: {
      cardListTitle: 'Things to do',
    },
    interfaceStyle: sdkFeatures.interfaceStyle || 'automatic',
    onRuntimeVariablesRequested: (cards, cb) => {
      const date = new Date()

      cards.forEach(card => {
        card.runtimeVariables.set('timestamp', date.getSeconds().toString())
        card.runtimeVariables.set('dateShort', date.toDateString())
        card.runtimeVariables.set('dateLong', date.toString())
      })

      cb(cards)
    },
    onLinkButtonPressed: payload => {
      alert(
        `A link button with a custom payload, defined in the Workbench, was tapped:\n\n${JSON.stringify(
          payload
        )}`
      )
    },
    onSubmitButtonPressed: payload => {
      alert(
        `A submit button with a custom payload, defined in the Workbench, was tapped:\n\n${JSON.stringify(
          payload
        )}`
      )
    },
    features: {
      //TODO: Remove deprecated config options once support for SDK version < 25.3.0 is no longer needed
      cardMinimumHeight: sdkFeatures.cardMinimumHeight,
      horizontalContainerConfig: {...sdkFeatures.horizontalContainerConfig},
      runtimeVariableAnalytics: sdkFeatures.runtimeVariableAnalytics,
    },
    // Relocated config options for SDK version 25.3.0+
    ...(hasFeature('modalStreamContainer', getSDKVersion())
      ? {
          cardMinimumHeight: sdkFeatures.cardMinimumHeight,
          cardMaximumWidth: sdkFeatures.cardMaximumWidth,
          cardHorizontalAlignment: sdkFeatures.cardHorizontalAlignment,
          horizontalContainerConfig: {...sdkFeatures.horizontalContainerConfig},
          modalContainerPositioning:
            sdkFeatures.modalContainerConfig.modalContainerPositioning,
          modalContainerVerticalPadding:
            sdkFeatures.modalContainerConfig.modalContainerVerticalPadding,
        }
      : {}),
  }

  if (embedMode === 'multiple') {
    return createMultipleContainers(config, onCardCountChanged)
  } else if (embedMode === 'standalone') {
    if (config.features && config.features.horizontalContainerConfig) {
      config.features.horizontalContainerConfig.enabled = false
    }
    if (config.horizontalContainerConfig) {
      config.horizontalContainerConfig.enabled = false
    }

    return window.AtomicSDK.embed(
      document.getElementById('embed'),
      config,
      sdkFeatures.autoresizeStandaloneFrame
    )
  } else if (embedMode === 'standalone-horizontal') {
    if (config.features && config.features.horizontalContainerConfig) {
      config.features.horizontalContainerConfig.enabled = true
    }
    if (config.horizontalContainerConfig) {
      config.horizontalContainerConfig.enabled = true
    }

    return window.AtomicSDK.embed(
      document.getElementById('horizontal-embed'),
      config,
      instance.sdkType === 'staging'
        ? sdkFeatures.autoresizeStandaloneFrame
        : true
    )
  } else if (embedMode === 'single-card' && window.AtomicSDK.singleCard) {
    // Current SDK version supports single card mode.
    return window.AtomicSDK.singleCard(document.getElementById('embed'), config)
  } else if (
    embedMode === 'headless' &&
    hasFeature('headlessStreamContainer', getSDKVersion())
  ) {
    return window.AtomicSDK.observableStreamContainer({
      ...config,
      cardFeedHandler,
    })
  } else if (
    embedMode === 'modal' &&
    hasFeature('modalStreamContainer', getSDKVersion())
  ) {
    return window.AtomicSDK.modalStreamContainer({
      ...config,
      onModalStreamToggled: isOpen => {
        if (isOpen) {
          console.log('Modal stream toggled open')
          document.body.classList.add('modal-stream-open')
        } else {
          console.log('Modal stream toggled closed')
          document.body.classList.remove('modal-stream-open')
        }
      },
    })
  } else {
    return window.AtomicSDK.launch(config)
  }
}

export {createStreamContainer}
